import ArchiveControlRoutes from '@account/components/archiveControl/ArchiveControlRoutes.jsx';
import ErrorPage from '@components/common/ErrorPage';
import Loading from '@components/common/Loading';
import React, {Suspense, lazy} from 'react';
import SecureRoute from './SecureRoute';
import {ORDERS_URL} from '@account/config';
import {Redirect, Route, Switch, useLocation} from 'react-router-dom';
import {Routes} from '@account/utils/route';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';

const AccountsPage = lazy(() => import('@components/accounts/AccountsPage'));
const AccountDetailPage = lazy(
  () => import('@components/accounts/AccountDetailPage')
);
const AllContractsPage = lazy(
  () => import('@components/provisioning/all-contracts/AllContractsPage')
);
const ContractDetailPage = lazy(
  () => import('@components/provisioning/contract-detail/ContractDetailPage')
);
const ContractValidationPage = lazy(
  () =>
    import('@components/provisioning/contract-validation/ContractValidationPage')
);
const ProvisioningOverviewPage = lazy(
  () =>
    import('@components/provisioning/provisioning-overview/ProvisioningOverviewPage')
);
const CreateCustomProvisionPage = lazy(
  () =>
    import('@components/provisioning/custom-provision/CreateCustomProvisionPage')
);
const LabsPage = lazy(() => import('@components/labs/LabsPage'));
const Organization = lazy(
  () => import('@components/organizations/Organization')
);
const OrgsPage = lazy(() => import('@components/organizations/OrgsPage'));
const PVContractDetailPage = lazy(
  () => import('@components/plans/PVContractDetailPage')
);
const PVLineItemPage = lazy(
  () => import('@components/common/pv-contracts/PVLineItemPage')
);
const PlanDetailPage = lazy(() => import('@components/plans/PlanDetailPage'));
const PlansPage = lazy(() => import('@components/plans/PlansPage'));
const ProductDetailPage = lazy(
  () => import('@components/products/ProductDetailPage')
);
const SentinelHubRedirectPage = lazy(
  () => import('@components/app/sentinel-hub/SentinelHubRedirectPage')
);
const TaskingContractDetailPage = lazy(
  () => import('@components/tasking/TaskingContractDetailPage')
);
const UserDashboardPage = lazy(
  () => import('@components/users/UserDashboardPage')
);
const UserProfilePage = lazy(() => import('@components/users/UserProfilePage'));
const UsersTablePage = lazy(() => import('@components/users/UsersTablePage'));
const MigrationWizard = lazy(
  () => import('@components/migration/MigrationWizard')
);
const OrgSetupWizard = lazy(
  () => import('@components/migration/OrgSetupWizard')
);
const FeatureUnavailablePage = lazy(
  () => import('@components/migration/FeatureUnavailablePage')
);

const AppRouter = () => {
  const auth = useAuth();
  const {pathname} = useLocation();
  const {
    deprecateContracts,
    deprecateContractsProvisioningOnly,
    showAllAccounts,
    migrationWizardAccess,
    manageArchiveControl,
  } = useFlags();

  const isOrgUser = hasRoleLevel(auth, [roleLevels.org_user]);
  const isOrgAdminOrPlaneteer = hasRoleLevel(auth, [
    roleLevels.org_admin,
    roleLevels.planet_user,
    roleLevels.planet_admin,
  ]);

  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        {/* Remove trailing slashes */}
        <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />

        {/* Planetary Variables redirects, set in place July 2023 */}
        <Redirect
          from="/planetary-variable-contracts/add-contract"
          to={Routes.CONTRACTS_ADD}
        />
        <Redirect
          from="/planetary-variable-contracts/:contractId/products/:lineItemId"
          to={Routes.CONTRACT_LINEITEM_DETAIL}
        />
        <Redirect
          from="/planetary-variable-contracts/:contractId"
          to={Routes.CONTRACT_DETAIL}
        />
        <Redirect from="/planetary-variable-contracts" to={Routes.CONTRACTS} />
        <Redirect
          from="/plans/planetary-variable-contract/:contractId/product/:lineItemId"
          to={Routes.PLAN_CONTRACT_LINEITEM_DETAIL}
        />
        <Redirect
          from="/plans/planetary-variable-contract/:contractId"
          to={Routes.PLAN_CONTRACT_DETAIL}
        />

        {/* Accounts */}

        {showAllAccounts && (
          <SecureRoute
            path={Routes.ACCOUNT_DETAIL}
            component={AccountDetailPage}
            roles={[roleLevels.planet_admin]}
          />
        )}

        {showAllAccounts && (
          <SecureRoute
            path={Routes.ACCOUNTS}
            component={AccountsPage}
            roles={[roleLevels.planet_admin]}
          />
        )}

        {/* Contracts */}

        {!deprecateContracts && (
          <SecureRoute
            path={Routes.CONTRACTS_ADD}
            component={CreateCustomProvisionPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContracts && (
          <SecureRoute
            path={Routes.CONTRACTS_ALL}
            component={AllContractsPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContracts && (
          <SecureRoute
            path={Routes.CONTRACT_LINEITEM_DETAIL}
            component={PVLineItemPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContractsProvisioningOnly && (
          <SecureRoute
            path={Routes.CONTRACT_VALIDATE}
            component={ContractValidationPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContractsProvisioningOnly && (
          <SecureRoute
            path={Routes.CONTRACT_DETAIL}
            component={ContractDetailPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContracts && (
          <SecureRoute
            path={Routes.CONTRACTS}
            component={ProvisioningOverviewPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {/* Dashboard */}

        <SecureRoute
          path={Routes.DASHBOARD}
          exact
          component={UserDashboardPage}
          roles={[roleLevels.org_user]} // for now only org users get this view
        />

        {/* Labs */}

        <SecureRoute
          path={Routes.LABS}
          component={LabsPage}
          roles={[roleLevels.planet_user, roleLevels.planet_admin]}
        />

        {/* Orders */}

        <Route
          path={Routes.ORDER_DETAIL}
          exact
          render={({match}) => {
            window.location.replace(`${ORDERS_URL}${match.params.orderId}/`);
          }}
        />

        <Route
          path={Routes.ORDERS}
          exact
          render={() => {
            window.location.replace(ORDERS_URL);
          }}
        />

        {/* Organizations */}

        <SecureRoute
          path={Routes.ORG_DETAIL}
          component={Organization}
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <SecureRoute
          path={Routes.ORGS}
          component={OrgsPage}
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        {/* Plans */}

        <Route path={Routes.PLAN_TASKING} exact>
          <Redirect to={`${Routes.PLANS}?tab=tasking`} />
        </Route>

        <Route path={Routes.PLAN_TASKING_DETAIL} exact>
          <TaskingContractDetailPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACT_DETAIL} exact>
          <PVContractDetailPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACT_LINEITEM_DETAIL} exact>
          <PVLineItemPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACTS} exact>
          <Redirect to={`${Routes.PLANS}?tab=pv`} />
        </Route>

        <Route path={Routes.PLAN_DETAIL} exact>
          <PlanDetailPage />
        </Route>

        <Route path={Routes.PLANS} component={PlansPage} />

        {/* Products */}

        <Route path={Routes.PRODUCT_DETAIL} component={ProductDetailPage} />

        {/* Provision */}

        {deprecateContracts && (
          <SecureRoute
            path={Routes.PROVISION_ADD}
            component={CreateCustomProvisionPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {deprecateContracts && (
          <SecureRoute
            path={Routes.PROVISION}
            component={ProvisioningOverviewPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {/* Users */}

        <SecureRoute
          path={Routes.USER_DETAIL}
          component={UserProfilePage}
          exact
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <SecureRoute
          path={Routes.USERS}
          component={UsersTablePage}
          exact
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <Route path={Routes.USER_SETTINGS} exact component={UserProfilePage} />

        {/* Migration Wizard */}

        <SecureRoute
          path={Routes.MIGRATION_EVAL_SETUP}
          component={
            migrationWizardAccess
              ? OrgSetupWizard
              : () => <FeatureUnavailablePage featureName="Migration Setup" />
          }
          exact
          roles={[roleLevels.planet_admin]}
        />

        <SecureRoute
          path={Routes.MIGRATION_WIZARD}
          component={
            migrationWizardAccess
              ? MigrationWizard
              : () => <FeatureUnavailablePage featureName="Migration Wizard" />
          }
          exact
          roles={[roleLevels.planet_admin]}
        />

        <Route
          path={Routes.SENTINEL_HUB_REDIRECT}
          exact
          component={SentinelHubRedirectPage}
        />

        {/* Archive Control */}
        {manageArchiveControl && (
          <SecureRoute
            path={Routes.ARCHIVE_CONTROL}
            component={ArchiveControlRoutes}
            roles={[roleLevels.planet_admin]}
          />
        )}

        {/* Root */}

        <Route path={Routes.ROOT} exact>
          {isOrgUser && <Redirect to={Routes.DASHBOARD} />}

          {isOrgAdminOrPlaneteer && <Redirect to={Routes.ORGS} />}

          <ErrorPage />
        </Route>

        <Route path="*">
          <ErrorPage statusCode={404} />
        </Route>
      </Switch>
    </Suspense>
  );
};

export default AppRouter;
